/**
 * AIInsightCard — the human-in-the-loop AI advisory card (FND-010).
 *
 * A distinct teal/brand-accented card: an "AI INSIGHT" label, a confidence
 * indicator, the advisory text, and Accept / Dismiss controls. Advisory-only —
 * the card surfaces a suggestion; the human confirms it. Designed to render
 * inline-right or as a bottom strip, never as the dominant element on a screen
 * (per the canonical anatomy, "Surfaced insights. Never always-visible.").
 */
import { useState, type CSSProperties, type ReactNode } from 'react';
import { colors, radius } from '../theme';

export interface AIInsightCardProps {
  /** The advisory text / recommendation. */
  children: ReactNode;
  /** Model confidence 0–1 (renders as High/Medium/Low + percent). */
  confidence?: number;
  /** Optional heading line above the advisory text. */
  heading?: string;
  /** Called when the operator accepts the recommendation. */
  onAccept?: () => void;
  /** Called when the operator dismisses it. */
  onDismiss?: () => void;
  /** Label for the accept control (default "Accept"). */
  acceptLabel?: string;
  /** Hide the action row (pure advisory display). */
  readOnly?: boolean;
  style?: CSSProperties;
}

function confidenceLabel(c: number): { label: string; color: string } {
  if (c >= 0.8) return { label: 'High', color: colors.success };
  if (c >= 0.5) return { label: 'Medium', color: colors.warning };
  return { label: 'Low', color: colors.danger };
}

export function AIInsightCard({
  children,
  confidence,
  heading,
  onAccept,
  onDismiss,
  acceptLabel = 'Accept',
  readOnly = false,
  style,
}: AIInsightCardProps) {
  const [resolved, setResolved] = useState<null | 'accepted' | 'dismissed'>(null);
  const conf = confidence !== undefined ? confidenceLabel(confidence) : null;

  const handleAccept = () => {
    setResolved('accepted');
    onAccept?.();
  };
  const handleDismiss = () => {
    setResolved('dismissed');
    onDismiss?.();
  };

  return (
    <div style={{ ...card, ...style }}>
      <div style={accentBar} />
      <div style={inner}>
        <div style={labelRow}>
          <span style={badge}>
            <span style={spark} aria-hidden>
              ✦
            </span>
            AI INSIGHT
          </span>
          {conf && (
            <span style={{ ...confChip, color: conf.color }}>
              <span style={{ ...confDot, background: conf.color }} />
              {conf.label} confidence
              {confidence !== undefined ? ` · ${Math.round(confidence * 100)}%` : ''}
            </span>
          )}
        </div>

        {heading && <div style={headingStyle}>{heading}</div>}
        <div style={text}>{children}</div>

        {!readOnly && (
          <div style={actions}>
            {resolved === null ? (
              <>
                <button type="button" onClick={handleAccept} style={acceptBtn}>
                  {acceptLabel}
                </button>
                <button type="button" onClick={handleDismiss} style={dismissBtn}>
                  Dismiss
                </button>
                <span style={hitlNote}>Advisory · requires your confirmation</span>
              </>
            ) : (
              <span style={resolvedNote}>
                {resolved === 'accepted' ? '✓ Accepted' : 'Dismissed'}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const card: CSSProperties = {
  position: 'relative',
  display: 'flex',
  background: '#F0FDFA', // very light teal wash
  border: `1px solid #99F6E4`,
  borderRadius: radius.md,
  overflow: 'hidden',
};
const accentBar: CSSProperties = { width: 4, background: colors.action, flexShrink: 0 };
const inner: CSSProperties = { padding: '14px 16px', flex: 1 };
const labelRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 8,
  flexWrap: 'wrap',
};
const badge: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  fontSize: '0.7rem',
  fontWeight: 800,
  letterSpacing: '0.06em',
  color: colors.action,
};
const spark: CSSProperties = { fontSize: '0.8rem' };
const confChip: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  fontSize: '0.74rem',
  fontWeight: 600,
};
const confDot: CSSProperties = { width: 7, height: 7, borderRadius: '50%' };
const headingStyle: CSSProperties = { fontWeight: 600, fontSize: '0.92rem', color: colors.textPrimary, marginBottom: 4 };
const text: CSSProperties = { fontSize: '0.88rem', color: colors.textPrimary, lineHeight: 1.5 };
const actions: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' };
const acceptBtn: CSSProperties = {
  padding: '5px 14px',
  background: colors.action,
  color: '#FFFFFF',
  border: 'none',
  borderRadius: radius.sm,
  fontSize: '0.82rem',
  fontWeight: 600,
  cursor: 'pointer',
};
const dismissBtn: CSSProperties = {
  padding: '5px 14px',
  background: '#FFFFFF',
  color: colors.textPrimary,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  fontSize: '0.82rem',
  fontWeight: 600,
  cursor: 'pointer',
};
const hitlNote: CSSProperties = { fontSize: '0.72rem', color: colors.textSecondary, marginLeft: 4 };
const resolvedNote: CSSProperties = { fontSize: '0.82rem', fontWeight: 600, color: colors.action };
